import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";

import { KTSVG } from "../../../_metronic/helpers";

import AddContractForm from "./AddContractForm";
import { useApi } from "../customHooks/useApi";

const API_URL = process.env.REACT_APP_API_URL
const MAX_FILE_SIZE = parseInt(process.env.REACT_APP_MAX_FILE_SIZE || "10", 10) * 1024 * 1024;

interface ContractModalProps {
    isOpen: boolean;
    onClose: () => void;
    companyId: number | null;
    mode: "add" | "edit" | "view";
    onSuccess: () => void;
    enterpriseName: string | null;
}

interface ContractResponse {
    id: number;
    company_id: number;

    contract_name: string;
    contract_number: string;
    contract_type: string;

    status: string;

    start_date: string;

    duration_value: number;
    duration_unit: string;

    auto_renew: boolean;

    contract_value: number;

    currency: string;

    billing_cycle: string;

    // support_plan: string;

    // account_manager_id: number | null;

    // technical_manager_id: number | null;

    signed_date: string;

    remarks: string;
    documents: any[];

    products: any[];
}

const today = new Date().toISOString().split("T")[0];

const initialFormData = {
    contract_name: "",
    contract_type: "",
    status: "Draft",
    start_date: today,
    duration_value: "",
    duration_unit: "Months",
    auto_renew: false,
    contract_value: "",
    currency: "INR",
    billing_cycle: "",
    signed_date: null,
    remarks: "",
    // documents: [],
    products: [],
};

function ContractModal({
    isOpen,
    onClose,
    companyId,
    mode,
    onSuccess,
    enterpriseName,
}: ContractModalProps) {
    const [formData, setFormData] = useState<any>(initialFormData);
    const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
    const [newDocuments, setNewDocuments] = useState<File[]>([]);
    const [submittedForm, setSubmittedForm] = useState(false);

    const handleDocumentsChange = (files: File[]) => {
        const validFiles: File[] = [];

        for (const file of files) {
            if (file.size > MAX_FILE_SIZE) {
                Swal.fire({
                    icon: "error",
                    title: "File too large",
                    text: `${file.name} exceeds the 10 MB limit.`,
                });
                continue;
            }

            const alreadyExists = newDocuments.some(
                f =>
                    f.name === file.name &&
                    f.size === file.size &&
                    f.lastModified === file.lastModified
            );

            if (alreadyExists) {
                continue;
            }

            validFiles.push(file);
        }

        setNewDocuments(prev => [...prev, ...validFiles]);
    };

    const [contractResponse, setContractResponse] =
        useState<ContractResponse>();

    const {
        data,
        loading,
        error,
        apiCall,
    }: any = useApi({
        method: "GET",
        url: companyId
            ? `${API_URL}/contracts/company/${companyId}`
            : "",
        enabled:
            isOpen &&
            (mode === "edit" || mode === "view") &&
            !!companyId,
        deps: [companyId, mode, isOpen],
        throwError: false
    });

    const [contractId, setContractId] =
        useState<number | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        if (mode === "add") {
            setSubmittedForm(false);
            setContractId(null);
            setContractResponse(undefined);
            setFormData(initialFormData);
        }
    }, [isOpen, mode]);

    useEffect(() => {
        if (!data || (mode !== "edit" && mode !== "view")) return;

        setContractResponse(data);

        setContractId(data.id);

        setFormData({
            contract_name: data.contract_name ?? "",
            contract_type: data.contract_type ?? "",
            status: data.status ?? "Draft",
            start_date: data.start_date ?? "",
            duration_value: data.duration_value ?? "",
            duration_unit: data.duration_unit ?? "Months",
            auto_renew: data.auto_renew ?? false,
            contract_value: data.contract_value ?? "",
            currency: data.currency ?? "INR",
            billing_cycle: data.billing_cycle ?? "",
            signed_date: data.signed_date ?? "",
            remarks: data.remarks ?? "",
            products: data.products ?? [],
        });

        setExistingDocuments(data.documents ?? []);

    }, [data, mode]);

    const handleInputChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement
        >
    ) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (
        name: string,
        value: any
    ) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        if (!formData.contract_name)
            return "Contract Name is required.";

        if (!formData.contract_type)
            return "Contract Type is required.";

        if (!formData.status)
            return "Status is required.";

        if (!formData.start_date)
            return "Start Date is required.";

        if (!formData.duration_value)
            return "Duration is required.";

        if (!formData.duration_unit)
            return "Duration Unit is required.";

        return null;
    };

    const handleSubmit = async () => {
        const errorMessage = validateForm();

        if (errorMessage) {
            Swal.fire({
                icon: "error",
                title: errorMessage,
            });
            return;
        }

        try {
            const body = new FormData();

            body.append(
                "payload",
                JSON.stringify(formData)
            );

            newDocuments.forEach(file => {
                body.append("documents", file);
            });

            const response =
                mode === "add"
                    ? await apiCall({
                        method: "POST",
                        url: `${API_URL}/contracts/company/${companyId}`,
                        payload: body,
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                        throwError: true,
                    })
                    : await apiCall({
                        method: "PUT",
                        url: `${API_URL}/contracts/company/${contractId}`,
                        payload: body,
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                        throwError: true,
                    });

            setContractResponse(response);

            setSubmittedForm(true);

            onSuccess();

            onClose();
        } catch (error: any) {
            Swal.fire({
                icon: "error",
                title:
                    error.response?.data?.detail ??
                    error.message ??
                    "Something went wrong.",
            });
        }
    };

    const handleReset = () => {
        setSubmittedForm(false);
        setContractId(null);
        setContractResponse(undefined);
        setExistingDocuments([]);
        setNewDocuments([]);
        setFormData(initialFormData);

        onClose();
    };

    return (
        <div
            className={`modal fade ${isOpen ? "show" : ""
                }`}
            tabIndex={-1}
            style={{
                display: isOpen ? "block" : "none",
            }}
        >
            <div
                className="modal-dialog modal-xl"
                style={{ marginTop: "5rem" }}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h3 className="modal-title">
                            {/* {mode === "add"
                                ? "Create Contract"
                                : "Edit Contract"} */}
                            {mode === "view" && "View Contract"}
                            {mode === "add" && "Create Contract"}
                            {mode === "edit" && "Edit Contract"}
                            <small className="text-muted ms-2 fw-normal">
                                ({enterpriseName})
                            </small>
                        </h3>

                        <div
                            className="btn btn-icon btn-sm btn-active-secondary"
                            onClick={handleReset}
                        >
                            <KTSVG
                                path="media/icons/duotune/arrows/arr061.svg"
                                className="svg-icon svg-icon-2x"
                            />
                        </div>
                    </div>

                    <div
                        className="modal-body"
                        style={{
                            maxHeight: "65vh",
                            overflow: "auto",
                        }}
                    >
                        {loading ? (
                            <div className="text-center py-10">
                                Loading...
                            </div>
                        ) : (
                            <AddContractForm
                                formData={formData}
                                onInputChange={
                                    handleInputChange
                                }
                                onSelectChange={
                                    handleSelectChange
                                }
                                submittedForm={
                                    submittedForm
                                }
                                readOnly={mode === "view"}
                                mode={mode}
                                onDocumentsChange={handleDocumentsChange}
                                newDocuments={newDocuments}
                                existingDocuments={existingDocuments}
                            />
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleReset}
                        >
                            Cancel
                        </button>

                        {mode !== "view" && (
                            <button
                                className="btn"
                                style={{
                                    backgroundColor: "#6D9300",
                                    color: "white",
                                }}
                                onClick={handleSubmit}
                            >
                                {mode === "add"
                                    ? "Create Contract"
                                    : "Update Contract"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContractModal;